import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { IoLockClosed } from 'react-icons/io5'
import type {
  LiveCheckinLevel,
  LiveCheckinLevelReward,
  LiveCheckinMonthLevelReward,
} from '../types/check-in'
import { formatNumber } from '../helpers/format'
import { rewardTypeLabel } from '../helpers/task-labels'

interface Props {
  reward: LiveCheckinLevelReward
  level: LiveCheckinLevel | undefined
  // Estado do mês: undefined = locked. Quando existe, traz unlocked/received.
  monthReward?: LiveCheckinMonthLevelReward
  onClaim?: (monthLevelRewardId: number) => void
  isClaiming?: boolean
}

export const RewardCard = memo(
  ({ reward, level, monthReward, onClaim, isClaiming }: Props) => {
    const { t } = useTranslation()
    const rewardName = reward.nameKey
      ? t(reward.nameKey, {
          defaultValue:
            reward.internalName ?? rewardTypeLabel(reward.liveCheckinRewardType),
        })
      : (reward.internalName ?? rewardTypeLabel(reward.liveCheckinRewardType))
    const isCoin = reward.liveCheckinRewardType === 'app_coin'
    const unlocked = !!monthReward?.unlocked
    const received = !!monthReward?.received

    const canClaim = unlocked && !received

    let stateLabel = 'Bloqueado'
    let stateChip = 'bg-white/10 text-white/70'
    if (received) {
      stateLabel = 'Resgatado'
      stateChip = 'bg-emerald-400 text-emerald-950'
    } else if (unlocked) {
      stateLabel = 'Disponível'
      stateChip = 'bg-amber-300 text-amber-950'
    }

    return (
      <div
        className={`relative flex flex-col items-stretch overflow-hidden rounded-xl border p-3 backdrop-blur-md transition ${
          received
            ? 'border-emerald-300/40 bg-emerald-500/10'
            : unlocked
              ? 'border-amber-300/40 bg-amber-400/10'
              : 'border-white/10 bg-white/5'
        }`}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">
            Nível {level?.level ?? '—'}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${stateChip}`}
          >
            {stateLabel}
          </span>
        </div>

        <div className="flex items-center gap-2.5">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg ${
              isCoin ? 'bg-amber-400/30' : 'bg-fuchsia-400/30'
            }`}
          >
            {!unlocked ? (
              <IoLockClosed className="h-5 w-5 text-white/70" />
            ) : isCoin ? (
              '🪙'
            ) : (
              '🎁'
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-white">
              {rewardName}
            </div>
            <div className="text-[11px] text-white/75">
              {isCoin
                ? `${formatNumber(reward.rewardAmount)} moedas`
                : `${rewardTypeLabel(reward.liveCheckinRewardType)} · ${
                    reward.durationInDay ? `${reward.durationInDay} dias` : 'permanente'
                  }`}
            </div>
          </div>
        </div>

        {canClaim && onClaim && (
          <button
            disabled={isClaiming}
            onClick={() => onClaim(monthReward!.id)}
            className="mt-3 w-full rounded-lg bg-gradient-to-r from-amber-300 to-orange-400 px-3 py-1.5 text-sm font-bold uppercase tracking-wide text-amber-950 shadow-sm transition active:scale-[0.98] disabled:opacity-60"
          >
            {isClaiming ? 'Resgatando…' : 'Resgatar'}
          </button>
        )}
      </div>
    )
  },
)

RewardCard.displayName = 'RewardCard'
